import React from 'react'
import V2Layout from '../components/V2Layout'
import SEO from '../components/SEO'
import CapabilitySections from './capabilities/CapabilitySections'
import useCompanyStats from '../components/useCompanyStats'

const FacilityV2Screen = () => {
    const { stats } = useCompanyStats()
    return (
        <V2Layout pageClass="pfc-facility-v2">
            <SEO
                title="Our Facility"
                description="Explore our state-of-the-art facilities equipped with advanced equipment, certified cleanrooms, and functional testing labs engineered for flight-critical fluid control components."
            />
            <section className="pfc-page-hero">
                <div className="pfc-page-hero-inner">
                    <p className="v2-section-label">Our Facility</p>
                    <h1 className="pfc-page-hero-title">Built for Precision Manufacturing</h1>
                    <p className="pfc-page-hero-sub">
                        Explore our state-of-the-art facilities equipped with advanced equipment, certified
                        cleanrooms, and functional testing labs engineered for flight-critical fluid control components.
                    </p>
                </div>
            </section>

            <section className="v2-stats-bar">
                <div className="v2-stats-inner">
                    <div className="v2-stat">
                        <span className="v2-stat-num">{stats.facility_size}</span>
                        <span className="v2-stat-label">Sq Ft Facility</span>
                    </div>
                    <div className="v2-stat">
                        <span className="v2-stat-num">{stats.cnc_total}</span>
                        <span className="v2-stat-label">CNC Work Centers</span>
                    </div>
                    <div className="v2-stat">
                        <span className="v2-stat-num">10K</span>
                        <span className="v2-stat-label">Cleanroom Class</span>
                    </div>
                    <div className="v2-stat">
                        <span className="v2-stat-num">100%</span>
                        <span className="v2-stat-label">Made in USA</span>
                    </div>
                </div>
            </section>

            {/* Dynamic sections managed by Admin console with zig-zag pattern */}
            <CapabilitySections slug="facility-v2" />

            {/* Statically designed Cleanroom & Contamination Control Showcase Section */}
            <section className="pfc-cleanroom-showcase" style={{ padding: '7rem 2rem', background: 'var(--v2-bg)', borderTop: '1px solid var(--v2-border)' }}>
                <div className="v2-content-inner" style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', gap: '5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div className="v2-content-col" style={{ flex: '1 1 450px' }}>
                        <p className="v2-section-label" style={{ color: 'var(--v2-primary)', letterSpacing: '0.15em', fontWeight: 700, textTransform: 'uppercase', marginBottom: '1rem' }}>Contamination Control</p>
                        <h2 className="v2-content-title" style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)', fontWeight: 900, color: 'var(--v2-primary)', marginBottom: '1.5rem', lineHeight: 1.15 }}>ISO Class 7 Clean Room Operations</h2>
                        <p className="v2-content-body">
                            Our third party-certified ISO 7 clean room environment features hard-wall construction,
                            HEPA filtration, and strict pressure cascades to ensure parts are assembled under the most
                            disciplined conditions.
                        </p>
                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
                            <span className="v2-cert-badge" style={{ background: 'var(--v2-border)', border: '1px solid var(--v2-border)', color: 'var(--v2-primary)', padding: '0.4rem 1rem', borderRadius: '4px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>ISO 7 Certified</span>
                            <span className="v2-cert-badge" style={{ background: 'var(--v2-border)', border: '1px solid var(--v2-border)', color: 'var(--v2-primary)', padding: '0.4rem 1rem', borderRadius: '4px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>NASA Approved</span>
                            <span className="v2-cert-badge" style={{ background: 'var(--v2-border)', border: '1px solid var(--v2-border)', color: 'var(--v2-primary)', padding: '0.4rem 1rem', borderRadius: '4px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Class 10k Equivalent</span>
                        </div>
                    </div>
                    
                    <div className="v2-content-col" style={{ flex: '1 1 380px' }}>
                        <div style={{
                            background: 'var(--v2-surface)',
                            color: 'var(--v2-text)',
                            padding: '3rem 2.5rem',
                            borderRadius: '12px',
                            boxShadow: '0 15px 35px rgba(0, 0, 0, 0.15)',
                            position: 'relative',
                            overflow: 'hidden',
                            border: '1px solid var(--v2-border)'
                        }}>
                            <div style={{ position: 'absolute', top: '-20px', right: '-20px', fontSize: '10rem', opacity: 0.03, color: 'var(--v2-primary)', fontWeight: 900, userSelect: 'none' }}>07</div>
                            <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1.5rem', color: 'var(--v2-primary)', borderBottom: '1px solid var(--v2-border)', paddingBottom: '1rem' }}>Controlled Specifications</h3>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                <li style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--v2-border)', paddingBottom: '0.75rem' }}>
                                    <span style={{ color: 'var(--v2-text2)', fontSize: '14px', fontWeight: 500 }}>Cleanroom Envelope</span>
                                    <strong style={{ color: 'var(--v2-text)', fontWeight: 700, fontSize: '14px' }}>{stats.cleanroom_size} Sq Ft</strong>
                                </li>
                                <li style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--v2-border)', paddingBottom: '0.75rem' }}>
                                    <span style={{ color: 'var(--v2-text2)', fontSize: '14px', fontWeight: 500 }}>Clean Work Area</span>
                                    <strong style={{ color: 'var(--v2-text)', fontWeight: 700, fontSize: '14px' }}>{stats.clean_work_area} Sq Ft (adjacent)</strong>
                                </li>
                                <li style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--v2-border)', paddingBottom: '0.75rem' }}>
                                    <span style={{ color: 'var(--v2-text2)', fontSize: '14px', fontWeight: 500 }}>Wall Construction</span>
                                    <strong style={{ color: 'var(--v2-text)', fontWeight: 700, fontSize: '14px' }}>Purpose-Built Hard-wall</strong>
                                </li>
                                <li style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--v2-border)', paddingBottom: '0.75rem' }}>
                                    <span style={{ color: 'var(--v2-text2)', fontSize: '14px', fontWeight: 500 }}>Cleanliness Validation</span>
                                    <strong style={{ color: 'var(--v2-text)', fontWeight: 700, fontSize: '14px' }}>IEST-STD-CC1246E Level 100+</strong>
                                </li>
                                <li style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: 'var(--v2-text2)', fontSize: '14px', fontWeight: 500 }}>Monitoring Controls</span>
                                    <strong style={{ color: 'var(--v2-text)', fontWeight: 700, fontSize: '14px' }}>Disciplined / Process-driven</strong>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
        </V2Layout>
    )
}

export default FacilityV2Screen
